import { DialogComponent, DialogCloseEvent } from '@theme/dialog';

const STORAGE_KEY = 'manceli:welcome-popup-hidden-until';
const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Welcome offer pop-up. Opens once after a delay, stays hidden for a number of days
 * after it is closed, and reopens straight away after a sign-up so the visitor sees
 * their code (or the form error).
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} [code] - The discount code, shown after a sign-up.
 * @property {HTMLButtonElement} [copyButton] - The button that copies the code.
 *
 * @extends DialogComponent
 */
class WelcomePopupComponent extends DialogComponent {
  /** @type {number | undefined} */
  #timer;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener(DialogCloseEvent.eventName, this.#rememberClosed);

    if (this.#justSubmitted) {
      this.showDialog();
      return;
    }

    if (this.#hiddenUntil > Date.now()) return;

    this.#timer = window.setTimeout(() => this.showDialog(), this.#delay);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.clearTimeout(this.#timer);
    this.removeEventListener(DialogCloseEvent.eventName, this.#rememberClosed);
  }

  /**
   * Copies the discount code to the clipboard.
   */
  copyCode = async () => {
    const { code, copyButton } = this.refs;
    if (!code || !copyButton) return;

    try {
      await navigator.clipboard.writeText(code.textContent?.trim() ?? '');
      copyButton.textContent = copyButton.dataset.copiedLabel ?? copyButton.textContent;
    } catch {
      // The clipboard can be blocked; select the code so it can be copied by hand.
      window.getSelection()?.selectAllChildren(code);
    }
  };

  get #delay() {
    return Number(this.dataset.delay || 8) * 1000;
  }

  get #hasSignedUp() {
    return this.querySelector('[data-welcome-success]') !== null;
  }

  /**
   * True on the page load that follows a sign-up from this pop-up or another email form.
   */
  get #justSubmitted() {
    const params = new URLSearchParams(window.location.search);
    if (params.get('customer_posted') === 'true' && this.#hasSignedUp) return true;

    return window.location.hash === `#${this.dataset.formId}`;
  }

  get #hiddenUntil() {
    try {
      return Number(window.localStorage.getItem(STORAGE_KEY)) || 0;
    } catch {
      return 0;
    }
  }

  #rememberClosed = () => {
    const days = this.#hasSignedUp ? 3650 : Number(this.dataset.daysHidden || 7);

    try {
      window.localStorage.setItem(STORAGE_KEY, String(Date.now() + days * DAY_MS));
    } catch {
      // Storage can be unavailable (private mode); the pop-up then shows again next visit.
    }
  };
}

if (!customElements.get('welcome-popup-component')) {
  customElements.define('welcome-popup-component', WelcomePopupComponent);
}
